/* ControlSourcesPage — §51-54 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { controlApi, useOnlineStatus } from "../lib/controlApi";
import { STATUS_STYLE } from "../ControlApp";
import { ChevronLeft, Loader2, RefreshCw } from "lucide-react";

export default function ControlSourcesPage() {
  const online = useOnlineStatus();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await controlApi.sources(); setItems(r.items || []); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (src) => {
    if (!online) { toast.error("Requiere conexión."); return; }
    try {
      await controlApi.patchSource(src.id, {
        enabled: src.status !== "active",
        expected_version: src.updated_at,
      });
      toast.success(src.status !== "active" ? "Fuente activada." : "Fuente pausada.");
      load();
    } catch (ex) {
      const code = ex && ex.response && ex.response.data && ex.response.data.detail && ex.response.data.detail.error && ex.response.data.detail.error.code;
      if (code === "VERSION_CONFLICT") { toast.error("Cambió desde otro dispositivo."); load(); }
      else toast.error("Error");
    }
  };

  const grouped = { A: [], B: [], C: [] };
  items.forEach((s) => {
    const t = (s.trust_level || "C").toUpperCase();
    if (grouped[t]) grouped[t].push(s); else grouped.C.push(s);
  });

  if (loading) return <div className="p-6 text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  return (
    <div className="p-4 space-y-3" data-testid="spc-sources">
      <div className="flex items-center gap-2">
        <Link to="/control/more" className="text-textgray"><ChevronLeft className="w-4 h-4" /></Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.35em] text-textgray">Fuentes</div>
          <div className="font-serif text-xl font-bold text-blueblack">{items.length} fuentes</div>
        </div>
        <button onClick={load} className="w-10 h-10 flex items-center justify-center border border-softblue bg-white"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {["A", "B", "C"].map((tier) => (
        grouped[tier].length > 0 && (
          <div key={tier} className="border border-softblue bg-white">
            <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">
              Fuentes {tier} · {tier === "A" ? "cada 30 min" : tier === "B" ? "cada 60 min" : "cada 2 h"}
            </div>
            <ul className="divide-y divide-softblue">
              {grouped[tier].map((src) => {
                const isPaused = src.status === "paused";
                const isDown = src.consecutive_failures_12h >= 5;
                const isDegraded = src.consecutive_failures_12h >= 2;
                const st = isPaused ? "paused" : isDown ? "critical" : isDegraded ? "degraded" : "operational";
                const s = STATUS_STYLE[st];
                return (
                  <li key={src.id} className="p-3 flex items-start gap-3">
                    <span className={`w-2 h-2 rounded-full mt-2 ${s.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-blueblack truncate">{src.name}</div>
                      <div className={`text-[11px] font-bold ${s.text}`}>{s.label}</div>
                      <div className="text-[10px] text-textgray mt-0.5">
                        {src.last_success_at ? <>Última consulta: {new Date(src.last_success_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}</> : "Sin consultas exitosas"}
                        {src.consecutive_failures_12h > 0 && <> · {src.consecutive_failures_12h} fallos 12h</>}
                      </div>
                    </div>
                    <button onClick={() => toggle(src)} data-testid={`spc-source-toggle-${src.id}`}
                      className={`h-8 px-3 border text-[10px] uppercase tracking-widest font-bold ${isPaused ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-softblue bg-white text-blueblack"}`}>
                      {isPaused ? "Activar" : "Pausar"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )
      ))}
    </div>
  );
}
